import React, {useState, useEffect} from "react";
import {CVTemplate, CVVersion} from "../types";
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from "@mui/material";
import {Translate as TranslateIcon} from "@mui/icons-material";
import 'dayjs/locale/it';
import {UtilityForm} from "../utils/UtilityForm";
import {CvVersionAPI} from "../services/API/CvVersionAPI";
import {apiService} from "../services/api";
import {useTranslation} from "react-i18next";
import {Log} from "../utils/Log";
import {Translate} from "../utils/Translate";

// ─────────────────────────────────────────────────────────────────────────────
const DEFAULT_LANG = 'original';

interface EditVersionSmartProps {
    item: CVVersion | null;
    onSave: () => void;
    onCancel: () => void;
    open: boolean;
    onClose: () => void;
    /** Lingue per cui esiste almeno una traduzione nel CV dell'utente.
     *  Passale dalla pagina padre per mostrare l'avviso "parziale".
     *  Se non passate, la combo è libera senza avvisi. */
    availableTranslatedLanguages?: string[];
}

export const EditVersionSmart: React.FC<EditVersionSmartProps> = ({
                                                                      item,
                                                                      onSave,
                                                                      onCancel,
                                                                      open,
                                                                      onClose,
                                                                      availableTranslatedLanguages = [],
                                                                  }) => {
    const [selectedSections, setSelectedSections] = useState<string[]>([]);
    const [templates, setTemplates] = useState<CVTemplate[]>([]);
    const [cvVersion, setCvVersion] = useState<CVVersion>({
        id: '', versionName: '', templateId: '', templateName: '',
        selectedSections: [], createdAt: '', updatedAt: ''
    });

    // ── Lingua selezionata per questa versione ──
    const [selectedLanguage, setSelectedLanguage] = useState<string>(DEFAULT_LANG);
    const [languageOptions, setLanguageOptions] = useState<{ code: string; i18nKey: string }[]>([]);

    const { t } = useTranslation();

    Log.writeLogDebug(item);

    const availableSections = [
        { id: 'personal',       label: t('dashboard.availableSectionPersonalData') },
        { id: 'education',      label: t('dashboard.availableSectionPersonalEducation') },
        { id: 'experience',     label: t('dashboard.availableSectionExperience') },
        { id: 'skills',         label: t('dashboard.availableSectionSkill') },
        { id: 'certifications', label: t('dashboard.availableSectionCertification') },
        { id: 'projects',       label: t('dashboard.availableSectionProject') },
        { id: 'language',       label: t('dashboard.availableSectionLanguage') },
    ];

    const setCvVersionDefault = () => {
        setCvVersion({
            id: '', versionName: '', templateId: '', templateName: '',
            selectedSections: [], createdAt: '', updatedAt: ''
        });
        setSelectedSections([]);
        setSelectedLanguage(DEFAULT_LANG);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // EFFECTS
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        Log.writeLogDebug('UseEffect');
        Log.writeLogDebug(item);
        if (open) {
            // Carica le lingue disponibili
            setLanguageOptions(Translate.LanguageForCombo());

            loadData().then(() => {
                if (item === null) {
                    setCvVersionDefault();
                } else {
                    Log.writeLogDebug(item);
                    setCvVersion(item);
                    setSelectedSections(item.selectedSections);
                    setSelectedLanguage((item as any).langCode ?? DEFAULT_LANG);
                }
            });
        }
    }, [item, open]);

    const loadData = async () => {
        try {
            setTemplates(await apiService.getTemplates());
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // SAVE
    // ─────────────────────────────────────────────────────────────────────────
    const handleSaveItem = async () => {
        try {
            const payload = {
                versionName: cvVersion.versionName,
                templateId: cvVersion.templateId,
                selectedSections: selectedSections,
                langCode: selectedLanguage === DEFAULT_LANG ? null : selectedLanguage,
            };

            Log.writeLogDebug(payload);
            if (!cvVersion.id) {
                await CvVersionAPI.Instance<CvVersionAPI>().createCVVersion(payload);
            } else {
                Log.writeLogDebug(cvVersion);
                await CvVersionAPI.Instance<CvVersionAPI>().updateCVVersion(cvVersion.id, payload);
            }

            setCvVersionDefault();
            onSave();
        } catch (error) {
            console.error('Error saving version:', error);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Lingua selezionata: è tradotta parzialmente o non tradotta?
    // ─────────────────────────────────────────────────────────────────────────
    const isNonOriginal = selectedLanguage !== DEFAULT_LANG;
    const isPartiallyTranslated =
        isNonOriginal &&
        availableTranslatedLanguages.length > 0 &&
        !availableTranslatedLanguages.includes(selectedLanguage);

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
            <DialogTitle>{t('cvVersionDialog.title')}</DialogTitle>

            <DialogContent>
                {/* ── Nome versione ── */}
                <TextField
                    autoFocus
                    margin="dense"
                    label={t('cvVersionDialog.versionName')}
                    fullWidth
                    variant="outlined"
                    value={cvVersion.versionName}
                    name="versionName"
                    onChange={(e) => UtilityForm.handleInputChange(e, setCvVersion)}
                    sx={{ mb: 2 }}
                />

                {/* ── Template ── */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>{t('cvVersionDialog.template')}</InputLabel>
                    <Select
                        value={cvVersion.templateId}
                        onChange={(e) => UtilityForm.handleInputChange(e, setCvVersion)}
                        label={t('cvVersionDialog.template')}
                        name="templateId"
                    >
                        {templates.map((template) => (
                            <MenuItem key={template.id} value={template.id}>
                                {template.name} {template.isPremium && '(Premium)'}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* ── Lingua del CV ── */}
                <FormControl fullWidth sx={{ mb: isNonOriginal ? 1 : 2 }}>
                    <InputLabel>{t('cvVersionDialog.language')}</InputLabel>
                    <Select
                        value={selectedLanguage}
                        onChange={(e) => setSelectedLanguage(e.target.value)}
                        label={t('cvVersionDialog.language')}
                        startAdornment={<TranslateIcon sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} />}
                    >
                        {/* Voce "Originale" sempre prima */}
                        <MenuItem value={DEFAULT_LANG}>
                            <em>{t('translation.originalLanguage')}</em>
                        </MenuItem>
                        <Divider />
                        {languageOptions.map(lang => (
                            <MenuItem key={lang.code} value={lang.code}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {t(lang.i18nKey)}
                                    {availableTranslatedLanguages.includes(lang.code) && (
                                        <Chip
                                            label={t('translation.alreadyTranslatedChip')}
                                            size="small"
                                            color="success"
                                            variant="outlined"
                                            sx={{ fontSize: '0.65rem', height: 18 }}
                                        />
                                    )}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* ── Avviso lingua non tradotta ── */}
                {isPartiallyTranslated && (
                    <Alert severity="warning" sx={{ mb: 2 }} icon={<TranslateIcon fontSize="inherit" />}>
                        {t('cvVersionDialog.languageWarning')}
                    </Alert>
                )}

                {/* ── Info lingua originale selezionata ── */}
                {!isNonOriginal && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        {t('cvVersionDialog.languageOriginalHint')}
                    </Typography>
                )}

                <Divider sx={{ mb: 2 }} />

                {/* ── Sezioni incluse ── */}
                <Typography variant="subtitle2" gutterBottom>
                    {t('cvVersionDialog.includingSection')}:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {availableSections.map((section) => (
                        <Chip
                            key={section.id}
                            label={section.label}
                            onClick={() => {
                                setSelectedSections(prev =>
                                    prev.includes(section.id)
                                        ? prev.filter(s => s !== section.id)
                                        : [...prev, section.id]
                                );
                            }}
                            color={selectedSections.includes(section.id) ? 'primary' : 'default'}
                            variant={selectedSections.includes(section.id) ? 'filled' : 'outlined'}
                        />
                    ))}
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>{t('standardUse.undoBtn')}</Button>
                <Button onClick={handleSaveItem} variant="contained">
                    {t('standardUse.saveBtn')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};